import React from "react";
import { Box, Paper, Typography, useTheme, Icon, alpha } from "@mui/material";
import { motion } from "framer-motion";

interface ChartContainerProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  delay?: number;
  height?: number;
  subtitle?: string;
  action?: React.ReactNode;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
}

export const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  icon,
  children,
  delay = 0,
  height = 300,
  subtitle,
  action,
  isLoading = false,
  emptyState,
}) => {
  const theme = useTheme();

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      sx={{
        p: {
          xs: 1.5,
          sm: 2,
          md: 2.5,
          lg: 3,
        },
        height: "100%",
        minHeight: {
          xs: 300,
          sm: 350,
          md: 400,
        },
        background: `linear-gradient(145deg, ${alpha(
          theme.palette.background.paper,
          0.9
        )}, ${alpha(theme.palette.background.paper, 0.95)})`,
        backdropFilter: "blur(8px)",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
        borderRadius: { xs: "8px", sm: "12px" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          mb: { xs: 1.5, sm: 2 },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 44,
              height: 44,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              mr: 2,
            }}
          >
            <Icon>{icon}</Icon>
          </Box> */}
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                  md: "1.125rem",
                  lg: "1.25rem",
                },
                lineHeight: 1.2,
                mb: { xs: 0.5, sm: 0.75 },
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: { xs: 0.25, sm: 0.5 },
                  fontSize: {
                    xs: "0.7rem",
                    sm: "0.75rem",
                    md: "0.8125rem",
                    lg: "0.875rem",
                  },
                  display: "block",
                  maxWidth: { xs: "100%", sm: "280px", md: "320px" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {action && (
          <Box
            sx={{
              ml: { sm: 2 },
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {action}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          height: {
            xs: 250,
            sm: 300,
            md: 350,
            lg: height,
          },
          position: "relative",
          flex: 1,
          ".apexcharts-text": {
            fontFamily: "inherit !important",
            fontSize: {
              xs: "10px !important",
              sm: "11px !important",
              md: "12px !important",
              lg: "13px !important",
            },
          },
          ".apexcharts-menu-item": {
            fontFamily: "inherit !important",
          },
          ".apexcharts-tooltip": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.1) !important",
          },
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                border: `3px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                borderTop: `3px solid ${theme.palette.primary.main}`,
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </Box>
        ) : emptyState ? (
          emptyState
        ) : (
          children
        )}
      </Box>
    </Paper>
  );
};
